// Pure adapter from CoreAPI's per-pose-nested `SeniorYearbookDetail` to the
// storefront's flat `YearbookContext` view-model.
//
// Side-effect-free by design: returns both the parsed `context` and a list
// of `errors` to be reported by the caller (the action that ingests the
// subject), so reporting fires once per subject load rather than once per
// reactive recompute of a Pinia getter.
//
// Data-inconsistency variants are surfaced — see inline numbered comments.
// The shape of each metadata object matches what the previous version emitted,
// so New Relic queries / saved views keyed on those fields keep working.
//
// `AvailableMediaItems` entries are self-describing
// (`YearbookPoseSelectableMediaDto`, carrying their own `ThumbPath`), so this
// adapter no longer joins MediaIds against the subject's profile media: yearbook
// media isn't necessarily part of that for-sale set. The old orphan variants
// (ids absent from subject media) are therefore gone; selection validation now
// checks membership in the pose's own `AvailableMediaItems`.
//
// Note: `AvailableMediaItems` (DTOs) is distinct from the additive
// `AvailableMedia: number[]` (raw MediaIds) on the same `YearbookPose`. This
// adapter consumes the DTO list because it needs each item's `ThumbPath`.

import type {
  YearbookContext,
  YearbookRequiredPose,
  YearbookSelectedImage,
  YearbookSelection
} from '@/types/YearbookContext'

import type {
  SubjectProfileReturn,
  YearbookPose
} from '@VelocitySquared/cady-sunny16-commerce-integration-models/core-api'

export interface YearbookContextError {
  error: Error
  metadata: Record<string, unknown>
}

export interface YearbookContextResult {
  context: YearbookContext | null
  errors: YearbookContextError[]
}

export function parseYearbookContext(
  subject: SubjectProfileReturn | null,
  cadySessionToken: string | null
): YearbookContextResult {
  const errors: YearbookContextError[] = []
  const yearbook = subject?.SeniorDetail?.Yearbook ?? null
  if (yearbook == null) return { context: null, errors }

  const poseMeta = (p: YearbookPose) => ({
    cadySessionToken,
    poseTypeId: p.PoseTypeId,
    poseTypeName: p.PoseTypeName,
    poseNumber: p.PoseNumber
  })

  // Human-readable pose identifier for error messages. Prefers the pose name;
  // falls back to the pose number / type id when the name is missing so the
  // message still points at a specific pose.
  const poseLabel = (p: YearbookPose) => {
    const name = p.PoseTypeName?.trim()
    return name ? `"${name}" (pose #${p.PoseNumber})` : `#${p.PoseNumber} (type ${p.PoseTypeId})`
  }

  const allowedPoses = (yearbook.Poses ?? []).filter((p) => p.Allowed)
  const requiredPoses: YearbookRequiredPose[] = []

  for (const p of allowedPoses) {
    const availableMediaItems = p.AvailableMediaItems ?? []

    // Variant 1: empty AvailableMediaItems.
    if (availableMediaItems.length === 0) {
      errors.push({
        error: new Error(`Yearbook pose ${poseLabel(p)} has empty AvailableMediaItems list`),
        metadata: poseMeta(p)
      })
      continue
    }

    requiredPoses.push({
      poseId: p.PoseTypeId,
      poseName: p.PoseTypeName ?? '',
      poseNumber: p.PoseNumber,
      eligibleMedia: availableMediaItems
    })
  }

  if (requiredPoses.length === 0) return { context: null, errors }

  // Selection-side validation. We only consider selections for poses that
  // survived the pose-level filter; selections on filtered-out poses were
  // already reported at the pose level.
  const selectedImages: YearbookSelectedImage[] = []
  const acceptedSelectionTimestamps: number[] = []

  for (const p of allowedPoses) {
    if (p.Selection == null) continue
    // Match on `poseNumber` (the unique per-pose ordinal), not `poseId`
    // (`PoseTypeId`, which repeats when a subject has multiple poses of the
    // same type). Matching on the type id would collide same-type poses onto
    // the first match.
    const requiredPose = requiredPoses.find((rp) => rp.poseNumber === p.PoseNumber)
    if (requiredPose == null) continue

    const selectionMediaId = p.Selection.MediaId
    const selectedMedia = requiredPose.eligibleMedia.find((m) => m.MediaId === selectionMediaId)

    // Variant 2 (formerly Variant 5): selection's MediaId isn't in the pose's
    // AvailableMediaItems (drift). Keep the selection — the customer made a
    // deliberate pick — but report it, and fall back to the selection's own
    // `MediaPath` for the thumbnail since the DTO list can't supply one.
    if (selectedMedia == null) {
      errors.push({
        error: new Error(
          `Yearbook pose ${poseLabel(p)} Selection MediaId not in AvailableMediaItems (selection drift)`
        ),
        metadata: { ...poseMeta(p), selectionMediaId, selectedOn: p.Selection.SelectedOn }
      })
    }

    selectedImages.push({
      imageId: selectionMediaId,
      poseId: p.PoseTypeId,
      poseName: p.PoseTypeName ?? '',
      poseNumber: p.PoseNumber,
      thumbUrl: selectedMedia?.ThumbPath ?? p.Selection.MediaPath ?? ''
    })
    acceptedSelectionTimestamps.push(new Date(p.Selection.SelectedOn).getTime())
  }

  let selection: YearbookSelection | null = null
  if (selectedImages.length > 0) {
    // Aggregate per-pose `SelectedOn` timestamps into one `selectedAt`: take
    // the most recent across the *accepted* selections. This conflates
    // "when each pose was assigned" with "when the form was submitted" —
    // acceptable for MVP; real CoreAPI may ship a single submission
    // timestamp once COR-006 lands.
    const mostRecentSelectedOn = acceptedSelectionTimestamps.reduce(
      (latest, current) => (current > latest ? current : latest),
      0
    )

    selection = {
      selectedImages,
      selectedAt: new Date(mostRecentSelectedOn).toISOString()
    }
  }

  return {
    context: {
      poseRequirements: { requiredPoses },
      selection,
      lastPhotoDate:
        yearbook.LastPhotoDate != null ? new Date(yearbook.LastPhotoDate).toISOString() : null
    },
    errors
  }
}
